import type { SetRequired } from 'type-fest';
import { z } from 'zod';

import { languageCodeSchema, languageCodeSchemaForJSA } from '../internationalization';
import { paginationSchema } from './pagination';
import { jobProfileTaskTransportSchema } from './profile-task';
import { jobProfileSkillSchema, skillSchema } from './skill';
import { relocationPreferenceSchema, userSchema } from './user';

export const assignableJobProfileReviewer = userSchema.pick({
    id: true,
    emailAddress: true,
    firstName: true,
    lastName: true,
});

export type AssignableJobProfileReviewer = z.infer<typeof assignableJobProfileReviewer>;

export const jobProfileReviewerCandidateSchema = z.object({
    id: z.string(),
    emailAddress: z.string().optional(),
    firstName: z.string(),
    lastName: z.string(),
});

export type JobProfileReviewerCandidate = z.infer<typeof jobProfileReviewerCandidateSchema>;

export const jobProfileReviewerCandidatesSchema = jobProfileReviewerCandidateSchema.array();

export type JobProfileReviewerCandidates = z.infer<typeof jobProfileReviewerCandidatesSchema>;

const jobProfileStateSchema = z.enum(['draft', 'active', 'archived']);

const translationTextSchema = z.object({
    locale: z.string(),
    text: z.string(),
});

export const sectionTagDefinitionSchema = z.object({
    id: z.string(),
    names: translationTextSchema.array(),
    type: z.string(),
});

export type SectionTagDefinition = z.infer<typeof sectionTagDefinitionSchema>;

export const jobProfileStructuredDescriptionSchema = z.object({
    id: z.string().optional(),
    tag: z
        .string()
        .regex(/^[a-z0-9][a-z0-9_]*$/, 'Tag must be lowercase and can only contain letters, numbers and underscores'),
    // Present only on task elements the backend synthesizes into TASKS-type template sections;
    // absent/null in template previews where tasks are not persisted.
    taskId: z.string().nullish(),
    value: z.string().max(100_000),
});

export type JobProfileStructuredDescription = z.infer<typeof jobProfileStructuredDescriptionSchema>;

export type SectionTitleIdentifier = Readonly<
    Pick<z.infer<typeof jobProfileStructuredDescriptionSchema>, 'id' | 'tag'>
>;

export const jobProfileTranslationsSchema = z.object({
    description: z.string().max(100_000).optional(),
    language: languageCodeSchemaForJSA,
    structured: jobProfileStructuredDescriptionSchema.array().optional(),
    title: z.string().max(256),
});

const jobProfileDescriptionGenerationResponseSchema = jobProfileTranslationsSchema
    .pick({ language: true, structured: true })
    .extend({ sectionTags: sectionTagDefinitionSchema.array().optional() });

export const jobProfileRegenerationResponseSchema = z.object({
    streamPath: z.string(),
});

export type JobProfileRegenerationResponse = z.infer<typeof jobProfileRegenerationResponseSchema>;

export type JobProfileDescriptionGenerationResponseContent = z.infer<
    typeof jobProfileDescriptionGenerationResponseSchema
>;

export const jobProfileDescriptionGenerationResponseArraySchema = jobProfileDescriptionGenerationResponseSchema.array();

export const jobArchitectureProfileReviewStateSchema = z.union([z.literal('assigned'), z.literal('approved')]);

export type JobArchitectureProfileReviewState = z.infer<typeof jobArchitectureProfileReviewStateSchema>;

export const jobArchitectureProfileReviewerSchema = jobProfileReviewerCandidateSchema.extend({
    state: jobArchitectureProfileReviewStateSchema,
});

export type JobArchitectureProfileReviewer = z.infer<typeof jobArchitectureProfileReviewerSchema>;

export const creatorSchema = z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
});

export type Person = z.infer<typeof creatorSchema>;

/** @deprecated Use Person instead */
export type Creator = Person;

export const jobProfileUpdatedBySchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
});

export const competencyTranslationSchema = z.object({
    language: z.string().max(5).min(5),
    title: z.string(),
});

const competencySchema = z.object({
    id: z.uuid(),
    title: z.string(),
    titles: competencyTranslationSchema.array(),
});

export type Competency = z.infer<typeof competencySchema>;

export const competenciesSchema = competencySchema.array().transform((competencies) => {
    return competencies.reduce((competencyMap, item) => {
        competencyMap.set(item.id, item);

        return competencyMap;
    }, new Map<string, Competency>());
});

export const getCompetencyTitle = (competency: Competency, languageCode?: string): string => {
    return competency.titles?.find((title) => title.language === languageCode)?.title ?? competency.title;
};

export type Competencies = z.infer<typeof competenciesSchema>;

export const jobProfileComponentSchema = z.object({
    id: z.uuid(),
    competencies: competenciesSchema.optional(),
    externalId: z.string().optional(),
    language: languageCodeSchema.optional(),
    tasks: jobProfileTaskTransportSchema.array().optional(),
    sectionTags: sectionTagDefinitionSchema.array().optional(),
    skills: jobProfileSkillSchema.array().optional(),
    structured: jobProfileStructuredDescriptionSchema.array().optional(),
    title: z.string(),
    translations: jobProfileTranslationsSchema.array(),
    updatedAt: z.string(),
    updatedBy: jobProfileUpdatedBySchema.optional(),
});

export type JobProfileComponent = z.infer<typeof jobProfileComponentSchema>;

export const jobArchitectureLayerSchema = z.enum(['root', 'family', 'cluster', 'role', 'level']);

export type JobArchitectureLayer = z.infer<typeof jobArchitectureLayerSchema>;

export const jobProfileLegacySchema = jobProfileComponentSchema.extend({
    actions: z
        .object({
            canAssignReviewer: z.boolean(),
            canEdit: z.boolean(),
        })
        .optional(),
    clusterId: z.uuid().nullish(),
    creator: creatorSchema.optional(),
    description: z.string().optional(),
    familyId: z.uuid().nullish(),
    jobArchitectureId: z.uuid().nullish(),
    layer: jobArchitectureLayerSchema.nullish(),
    levelId: z.uuid().nullish(),
    profileGenerationInfo: z
        .object({
            inProgress: z.boolean(),
        })
        .optional(),
    reviewer: jobArchitectureProfileReviewerSchema.optional(),
    roleId: z.uuid().nullish(),
    state: jobProfileStateSchema,
});

export type JobProfileLegacy = z.infer<typeof jobProfileLegacySchema>;

export type JobProfileLegacyWithReviewer = SetRequired<JobProfileLegacy, 'reviewer'>;

export const jobProfileForGenerationWithPromptSchema = jobProfileComponentSchema
    .pick({
        externalId: true,
        skills: true,
        translations: true,
    })
    .transform((jobProfile) => ({
        ...jobProfile,
    }));

export type JobProfileForGenerationWithPrompt = z.infer<typeof jobProfileForGenerationWithPromptSchema>;

export const createJobProfileForGenerationWithPrompt = (
    jobProfile: JobProfileComponent | JobProfileLegacy,
): JobProfileForGenerationWithPrompt => ({
    externalId: jobProfile.externalId,
    skills: jobProfile.skills,
    translations: jobProfile.translations,
});

export const jobProfilesOptionsResponseSchema = z.object({
    readMany: z.object({
        enabled: z.boolean(),
        sortableBy: z.string().array(),
    }),
    skillLimit: z.number(),
    structuredTags: z.string().array(),
});

export type JobProfilesOptionsResponse = z.infer<typeof jobProfilesOptionsResponseSchema>;

export const jobProfilesPermissionsSchema = z.object({
    canUserGetJobProfiles: z.boolean(),
});

export type JobProfilesPermissions = z.infer<typeof jobProfilesPermissionsSchema>;

export const jobProfilesResponseSchema = z.object({
    data: jobProfileLegacySchema.array(),
    pagination: paginationSchema,
});

export type JobProfilesResponse = z.infer<typeof jobProfilesResponseSchema>;

export const jobProfileAssignmentsCountResponseSchema = z.object({
    pagination: z.object({
        count: z.number().nonnegative(),
    }),
});

export type JobProfileAssignmentsCountResponse = z.infer<typeof jobProfileAssignmentsCountResponseSchema>;

const nonEmptyString = z.string().min(1);
const optionalNonEmptyString = nonEmptyString.optional();

export const jobProfileAssignmentsResponseSchema = z.object({
    data: z
        .object({
            id: z.uuid(),
            assignedBy: optionalNonEmptyString,
            firstName: optionalNonEmptyString,
            jobTitle: z.string().optional(),
            lastName: optionalNonEmptyString,
            relocationPreference: relocationPreferenceSchema.optional(),
            skills: skillSchema.array(),
        })
        .array(),
    pagination: paginationSchema,
});

export type JobProfileAssignment = z.infer<typeof jobProfileAssignmentsResponseSchema>['data'][number];

export const userProfileAssignmentsResponseSchema = jobProfileLegacySchema.array();

export type UserProfileAssignmentsResponse = z.infer<typeof userProfileAssignmentsResponseSchema>;

export const userProfileAssignmentsOptionsSchema = z.object({
    canReadUserProfileAssignments: z.boolean(),
});
export type UserProfileAssignmentsOptions = z.infer<typeof userProfileAssignmentsOptionsSchema>;
